// Blog article data - lazy loaded per language
// Types and helpers are synchronous; article data loads on demand

// Re-export types for consumers
export type { BlogArticle, BlogSection, BlogLanguage } from './blog/types';
export { blogImage } from './blog/types';

import type { BlogArticle } from './blog/types';

// Cache for loaded article sets
const articleCache = new Map<string, BlogArticle[]>();

/**
 * Load articles for a given language using dynamic import().
 * Each language's data lives in its own chunk and is only fetched when needed.
 */
async function loadArticlesForLanguage(language: string): Promise<BlogArticle[]> {
  if (articleCache.has(language)) return articleCache.get(language)!;

  let articles: BlogArticle[];

  switch (language) {
    case 'de': {
      const [mod, enMod] = await Promise.all([import('./blog/articles-de'), import('./blog/articles-en')]);
      const deArticles = mod.articlesDe;
      const translatedSlugs = new Set(deArticles.map((a: BlogArticle) => a.slug));
      const uniqueOriginals = enMod.articlesEn.filter((a: BlogArticle) => a.language === 'de' && !translatedSlugs.has(a.slug));
      articles = [...deArticles, ...uniqueOriginals];
      break;
    }
    case 'es': {
      const [mod1, mod2] = await Promise.all([import('./blog/articles-es'), import('./blog/articles-es-batch2')]);
      articles = [...mod1.articlesEs, ...mod2.articlesEsBatch2];
      break;
    }
    case 'fr': {
      const [mod1, mod2] = await Promise.all([import('./blog/articles-fr'), import('./blog/articles-fr-batch2')]);
      articles = [...mod1.articlesFr, ...mod2.articlesFrBatch2];
      break;
    }
    case 'pt': {
      const [mod1, mod2] = await Promise.all([import('./blog/articles-pt'), import('./blog/articles-pt-batch2')]);
      articles = [...mod1.articlesPt, ...mod2.articlesPtBatch2];
      break;
    }
    case 'ru': {
      const [mod1, mod2] = await Promise.all([import('./blog/articles-ru'), import('./blog/articles-ru-batch2')]);
      articles = [...mod1.articlesRu, ...mod2.articlesRuBatch2];
      break;
    }
    case 'it': {
      const [mod1, mod2] = await Promise.all([import('./blog/articles-it'), import('./blog/articles-it-batch2')]);
      articles = [...mod1.articlesIt, ...mod2.articlesItBatch2];
      break;
    }
    case 'sv': {
      const [mod1, mod2, mod3] = await Promise.all([import('./blog/articles-sv'), import('./blog/articles-sv-batch2'), import('./blog/articles-sv-batch3')]);
      articles = [...mod1.articlesSv, ...mod2.articlesSvBatch2, ...mod3.articlesSvBatch3];
      break;
    }
    case 'zh': {
      const [mod1, mod2, mod3] = await Promise.all([import('./blog/articles-zh'), import('./blog/articles-zh-batch2'), import('./blog/articles-zh-batch3')]);
      articles = [...mod1.articlesZh, ...mod2.articlesZhBatch2, ...mod3.articlesZhBatch3];
      break;
    }
    default: {
      const en = await import('./blog/articles-en');
      articles = en.articlesEn.filter((a: BlogArticle) => a.language === 'en');
      break;
    }
  }

  articleCache.set(language, articles);
  return articles;
}

/**
 * Get articles filtered by language (async — lazy loads data).
 */
export async function getArticlesByLanguage(language: string): Promise<BlogArticle[]> {
  return loadArticlesForLanguage(language);
}

/**
 * Get a single article by slug, respecting language (async).
 */
export async function getArticleBySlug(slug: string, language?: string): Promise<BlogArticle | undefined> {
  if (language) {
    const langArticles = await getArticlesByLanguage(language);
    const found = langArticles.find(a => (a.localizedSlug || a.slug) === slug || a.slug === slug);
    if (found) return found;
  }
  return undefined;
}

/** Helper: get the URL-facing slug for an article */
export function getArticleUrlSlug(article: BlogArticle): string {
  return article.localizedSlug || article.slug;
}

/**
 * Get related articles (exclude current, same language, max count) — async.
 */
export async function getRelatedArticles(currentSlug: string, language: string, count = 3): Promise<BlogArticle[]> {
  const articles = await getArticlesByLanguage(language);
  return articles.filter(a => a.slug !== currentSlug && (a.localizedSlug || a.slug) !== currentSlug).slice(0, count);
}
